import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LoadingOverlay, useAlert } from '../../components';
import { getProducts, purchaseProduct, getUserProducts } from '../../services/firestoreService';
import { formatCurrency } from '../../utils/helpers';
import './ProductsScreen.css';

export const ProductsScreen = () => {
    const navigate = useNavigate();
    const { userData, refreshUserData } = useAuth();
    const alert = useAlert();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(null);
    const [activeTab, setActiveTab] = useState('ALL');

    useEffect(() => {
        loadProducts();
    }, []);

    const loadProducts = async () => {
        try {
            const data = await getProducts();
            setProducts(data);
        } catch (error) {
            console.error('Error loading products:', error);
            alert.showError('Error', 'Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    const handlePurchase = async (product) => {
        if (!userData) return;

        // Check purchase limit
        try {
            const userProducts = await getUserProducts();
            const purchaseCount = userProducts.filter(p => p.productId === product.id).length;
            const maxPurchases = product.maxPurchases || 10;

            if (purchaseCount >= maxPurchases) {
                alert.showError('Purchase Limit Reached', `You can only purchase this product ${maxPurchases} time(s). You have already purchased it ${purchaseCount} time(s).`);
                return;
            }
        } catch (error) {
            console.error('Error checking purchase limit:', error);
        }

        if (userData.rechargeBalance < product.price) {
            alert.showConfirm(
                'Insufficient Balance',
                `You need ${formatCurrency(product.price - userData.rechargeBalance)} more to purchase this product.`,
                () => navigate('/recharge'),
                undefined
            );
            return;
        }

        alert.showConfirm(
            'Confirm Purchase',
            `Purchase ${product.name} for ${formatCurrency(product.price)}?`,
            async () => {
                setPurchasing(product.id);
                try {
                    await purchaseProduct(product);
                    await refreshUserData();
                    alert.showSuccess('Success', `Successfully purchased ${product.name}!`);
                } catch (error) {
                    alert.showError('Error', error.message || 'Failed to purchase product');
                } finally {
                    setPurchasing(null);
                }
            }
        );
    };

    const filteredProducts = products.filter(product => {
        if (activeTab === 'ALL') return true;
        if (activeTab === 'VIP') return product.category === 'VIP';
        if (activeTab === 'EVENT') return product.category === 'EVENT';
        return true;
    });

    const getDefaultImage = (index) => {
        const images = [
            'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
        ];
        return images[index % images.length];
    };

    if (loading) {
        return <LoadingOverlay visible message="Loading products..." />;
    }

    return (
        <div className="products-screen">
            <div className="products-header">
                <h1 className="products-title">INVESTMENT PLANS</h1>
            </div>

            <div className="products-tabs">
                <button
                    className={`tab-btn ${activeTab === 'ALL' ? 'active' : ''}`}
                    onClick={() => setActiveTab('ALL')}
                >
                    GLOBAL
                </button>
                <button
                    className={`tab-btn ${activeTab === 'VIP' ? 'active' : ''}`}
                    onClick={() => setActiveTab('VIP')}
                >
                    VIP ENERGY
                </button>
                <button
                    className={`tab-btn ${activeTab === 'EVENT' ? 'active' : ''}`}
                    onClick={() => setActiveTab('EVENT')}
                >
                    EVENTS
                </button>
            </div>

            <div className="wallet-balance-card">
                <span className="wallet-label">WALLET BALANCE</span>
                <span className="wallet-amount">{formatCurrency(userData?.rechargeBalance || 0)}</span>
            </div>

            <div className="products-list">
                {filteredProducts.map((product, index) => (
                    <div key={product.id} className="product-card-modern">
                        <div className="card-top-header">
                            <h2 className="modern-product-name">{product.name}</h2>
                        </div>

                        <div className="card-content-layout">
                            <div className="modern-image-container">
                                <div className="modern-type-badge">TYPE-P{index + 1}</div>
                                <img
                                    src={product.imageUrl || getDefaultImage(index)}
                                    alt={product.name}
                                    className="modern-product-image"
                                />
                            </div>

                            <div className="modern-details-grid">
                                <div className="modern-detail-item">
                                    <span className="modern-detail-label">PRICE</span>
                                    <span className="modern-detail-value">{formatCurrency(product.price)}</span>
                                </div>
                                <div className="modern-detail-item">
                                    <span className="modern-detail-label">DAILY</span>
                                    <span className="modern-detail-value highlight">{formatCurrency(product.dailyIncome)}</span>
                                </div>
                                <div className="modern-detail-item">
                                    <span className="modern-detail-label">TERM</span>
                                    <span className="modern-detail-value">{product.validity} Days</span>
                                </div>
                                <div className="modern-detail-item">
                                    <span className="modern-detail-label">LIMIT</span>
                                    <span className="modern-detail-value">{product.maxPurchases || 10}pcs</span>
                                </div>
                            </div>
                        </div>

                        <div className="modern-progress-section">
                            <div className="modern-progress-info">
                                <span className="modern-progress-label">SOLD CAPACITY</span>
                                <span className="modern-progress-percentage">{product.soldCount || 35}%</span>
                            </div>
                            <div className="modern-progress-bar-bg">
                                <div
                                    className="modern-progress-fill"
                                    style={{ width: `${Math.min((product.soldCount || 35), 100)}%` }}
                                ></div>
                            </div>
                        </div>

                        <button
                            className="modern-invest-btn"
                            onClick={() => handlePurchase(product)}
                            disabled={purchasing === product.id}
                        >
                            <span className="material-icons">bolt</span>
                            {purchasing === product.id ? 'PROCESSING...' : 'INVEST NOW'}
                        </button>
                    </div>
                ))}
            </div>

            {filteredProducts.length === 0 && (
                <div className="empty-state">
                    <span className="material-icons empty-icon">inventory_2</span>
                    <h3>No Products Available</h3>
                    <p>Check back later for new investment opportunities</p>
                </div>
            )}
        </div>
    );
};

export default ProductsScreen;
